import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'

import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NewBuildingAccessModalBody } from 'pages/costs/modals/NewBuildingAccessModalBody'
import { colsBuildingAccess } from './common/CostsColData'
import { withDownloadNotificationNames } from './common/downloadNotificationIntl'
import { useColumnTextFilter } from './common/tableTextFilter'
import { useTableSort } from './common/useTableSort'
import {
	buildEmptyPagedResponse,
	getCountryIdsFromTableFilter,
	mergeCountryFilters,
	useCostsCountryCatalog,
} from './common/useCostsCountryFilters'
import { UploadExcelModalBody } from './modals/excel/UploadExcelModalBody'

import { deepEqual } from 'fast-equals'

import { DataTablePFSEvent } from 'primereact/datatable'
import { toast } from 'react-toastify'
import {
	colorConstants,
	ColData,
	HardFilterWrapper,
	HardFilterWrapperHandle,
	MultipleDeleter,
	Table,
	UfinetActionButtonHandle,
	UfinetBox,
	UfinetButton,
	UfinetModal,
	UfinetSectionBox,
} from 'ufinet-web-components'
import {
	AreaType,
	AuthContext,
	Authority,
	buildingAccessCostsService,
	defaultPageNumber,
	defaultPageSize,
	EmptyPagingData,
	getTableTranslations,
	ICostsBuildingAccessResponse,
	ICostsQueryCriteria,
	mapFromPagedResponseToPagingData,
	onSelectionChangePreventDefaultsRemoval,
	PagedResponse,
	PagingData,
	useAsync,
	useModal,
} from 'ufinet-web-functions'

type PagedBuildingAccessResponse = PagedResponse<ICostsBuildingAccessResponse>

const BuildingAccessPage: FC = () => {
	const intl = useIntl()
	const searchButtonRef = useRef<UfinetActionButtonHandle>()
	// Guards against an in-flight, slower search overwriting the results of a newer one (e.g. clicking
	// Buscar right after typing a filter): only the response of the latest request is applied.
	const requestIdRef = useRef(0)
	const [buildingAccessResponse, setBuildingAccessResponse] = useState<ICostsBuildingAccessResponse[]>([])
	const { sortField, sortOrder, handleSort, sortData } = useTableSort<ICostsBuildingAccessResponse>()
	const [pagingData, setPagingData] = useState<PagingData>(EmptyPagingData)
	const [pagingState, setPagingState] = useState({ pageNumber: defaultPageNumber, pageSize: defaultPageSize })
	const [modal, showModal, hideModal] = useModal()
	const [newModal, showNewModal, hideNewModal] = useModal()
	const [selectedValues, setSelectedValues] = useState<ICostsBuildingAccessResponse[]>()
	const [tableCountryFilterIds, setTableCountryFilterIds] = useState<string[]>([])
	const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
	const authData = useContext(AuthContext)
	const { countryFilterOptions } = useCostsCountryCatalog(authData)
	const roles = authData.userData?.authorities || []
	const permissions = Authority.getCostsPermissions(roles)
	const _buildingAccessCostsService = useMemo(() => buildingAccessCostsService(authData, intl), [authData, intl])

	// Free-text Cliente/Área/Actualizado por filters: see tableTextFilter.tsx for why (broken built-in select match mode).
	const clientNameFilter = useColumnTextFilter('COST.CLIENT')
	const areaNameFilter = useColumnTextFilter('COST.AREA')
	const updatedByFilter = useColumnTextFilter('COST.UPDATEDBY')

	const cols: ColData[] = useMemo(() => {
		return colsBuildingAccess.map((column) => {
			if (column.field === 'countryName') return { ...column, filterOptions: countryFilterOptions }
			if (column.field === 'clientId') return clientNameFilter.applyToColumn(column)
			if (column.field === 'networkAreaName') return areaNameFilter.applyToColumn(column)
			if (column.field === 'lastModifiedBy') return updatedByFilter.applyToColumn(column)
			return column
		})
	}, [
		countryFilterOptions,
		clientNameFilter.applyToColumn,
		areaNameFilter.applyToColumn,
		updatedByFilter.applyToColumn,
	])

	const upperFilterRef = useRef<HardFilterWrapperHandle>(null)

	// Reset to page 1 whenever the free-text Cliente/Área/Actualizado por search term changes, so results
	// from the new search aren't hidden behind a page number that no longer makes sense.
	useEffect(() => {
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
	}, [clientNameFilter.value, areaNameFilter.value, updatedByFilter.value])

	const getBuildingAccessCosts = useCallback(async (): Promise<PagedBuildingAccessResponse | null> => {
		const requestId = ++requestIdRef.current
		setLoadingRecords(true)
		searchButtonRef.current?.changeActionStatus(true)
		const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
		const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(hardFilterCountryIds, tableCountryFilterIds)
		if (hasImpossibleCountryFilter) {
			return buildEmptyPagedResponse<ICostsBuildingAccessResponse>(pagingState.pageNumber, pagingState.pageSize)
		}

		// Cliente/Área/Actualizado por are searched server-side (LIKE '%term%', see aqs-backend's
		// clientNameSearch/networkAreaNameSearch/updatedBy) so results cover the whole dataset, not just this page.
		const criteria: ICostsQueryCriteria & {
			pageNumber: number
			pageSize: number
			clientNameSearch?: string
			networkAreaNameSearch?: string
			updatedBy?: string
		} = {
			countries,
			networkAreas: upperFilterRef.current?.getAreas().map((e) => e.value) || [],
			pageNumber: pagingState.pageNumber,
			pageSize: pagingState.pageSize,
			clientNameSearch: clientNameFilter.value,
			networkAreaNameSearch: areaNameFilter.value,
			updatedBy: updatedByFilter.value,
		}
		const result = (await _buildingAccessCostsService.findByCriteria(
			criteria
		)) as unknown as PagedBuildingAccessResponse
		return requestId === requestIdRef.current ? result : null
	}, [
		_buildingAccessCostsService,
		pagingState.pageNumber,
		pagingState.pageSize,
		tableCountryFilterIds,
		clientNameFilter.value,
		areaNameFilter.value,
		updatedByFilter.value,
	])

	const onBuildingAccessFetched = (response: PagedBuildingAccessResponse | null): void => {
		if (!response) return // superseded by a newer search; ignore this stale response
		const items = Array.isArray(response) ? (response as unknown as ICostsBuildingAccessResponse[]) : response.data
		const parsedCosts = items.map((cost: ICostsBuildingAccessResponse) => ({
			...cost,
			networkAreaName: getTableTranslations(cost.networkAreaName, intl),
		}))
		if (!Array.isArray(response)) {
			setPagingData(mapFromPagedResponseToPagingData(response))
		}
		setBuildingAccessResponse(parsedCosts)
	}

	const onBuildingAccessFailedToFetch = (): void => {
		toast.error(intl.formatMessage({ id: 'COSTS.FETCH.ERROR' }))
	}

	const runFinally = () => {
		setLoadingRecords(false)
		searchButtonRef.current?.changeActionStatus(false)
	}

	const fetchAndStore = useAsync(
		{
			asyncFn: getBuildingAccessCosts,
			onSuccess: onBuildingAccessFetched,
			onFailure: onBuildingAccessFailedToFetch,
			runFinally: runFinally,
		},
		[
			pagingState.pageNumber,
			pagingState.pageSize,
			tableCountryFilterIds,
			clientNameFilter.value,
			areaNameFilter.value,
			updatedByFilter.value,
		]
	)

	const handlePage = (event: DataTablePFSEvent) => {
		setPagingState({ pageNumber: event.page ?? defaultPageNumber, pageSize: event.rows ?? defaultPageSize })
	}
	const handleFilter = (event: DataTablePFSEvent) => {
		const countryIds = getCountryIdsFromTableFilter(event)
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
		setTableCountryFilterIds(countryIds)
	}

	const getActionButtons = () => {
		return (
			<>
				<OverlayTrigger
					placement="bottom"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip className="tooltip-dark">{intl.formatMessage({ id: 'DOWNLOAD_EXCEL' })}</Tooltip>}
				>
					<div className="d-inline">
						<FontAwesomeIcon
							className="fa-icon-primary"
							size="1x"
							icon={faDownload}
							onClick={() => {
								const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
								const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(
									hardFilterCountryIds,
									tableCountryFilterIds
								)
								if (hasImpossibleCountryFilter) return
								const areaOptions = upperFilterRef.current?.getAreas() || []
								const idToName = new Map([
									...(countryFilterOptions?.map((o): [string, string] => [o.value, o.label]) || []),
									...areaOptions.map((o): [string, string] => [o.value, o.label]),
								])
								buildingAccessCostsService(authData, withDownloadNotificationNames(intl, idToName)).downloadXLS({
									countries,
									networkAreas: areaOptions.map((e) => e.value),
								})
							}}
						/>
					</div>
				</OverlayTrigger>
			</>
		)
	}

	const getHeaderButtons = () => (
		<>
			{permissions.canDelete && (
				<MultipleDeleter
					setSelectedValues={setSelectedValues}
					selectedValues={selectedValues}
					deleteEndpoint={_buildingAccessCostsService.deleteByIdsList}
					search={fetchAndStore}
				/>
			)}
			{permissions.canWrite && (
				<>
					<UfinetButton
						className="me-3"
						icon={faPlus}
						onClick={() => {
							showNewModal()
						}}
						content={intl.formatMessage({ id: 'NEW.REGISTER' })}
					/>
					<UfinetButton
						icon={faUpload}
						onClick={() => {
							showModal()
						}}
						content={intl.formatMessage({ id: 'BULK_LOAD' })}
					/>
				</>
			)}
		</>
	)

	const onRowEditComplete = (data: ICostsBuildingAccessResponse) => {
		if (
			deepEqual(
				data,
				buildingAccessResponse.find((it) => it.id === data.id)
			)
		)
			return

		Swal.fire({
			icon: 'warning',
			title: intl.formatMessage({ id: 'COUNTRY.REGISTRY.UPDATE' }, { countryName: data.countryName }),
			html: intl.formatMessage({ id: 'COUNTRY.REGISTRY.UPDATE.CONFIRM' }, { networkAreaName: data.networkAreaName }),
			showCancelButton: true,
			showConfirmButton: true,
			reverseButtons: true,
			confirmButtonColor: colorConstants.primaryColor,
			cancelButtonColor: colorConstants.dangerColor,
			preConfirm: () => {
				const {
					id,
					countryName,
					networkAreaName,
					punctualCost,
					monthlyCost,
					monthlyCostPerCapacity,
					clientId,
					ceConnect,
					distanceFo,
				} = data
				_buildingAccessCostsService
					.update(id, {
						countryName,
						networkAreaName,
						punctualCost,
						monthlyCost,
						monthlyCostPerCapacity,
						clientId,
						ceConnect,
						distanceFo,
					})
					.then(fetchAndStore)
					.catch(console.error)
			},
		})
	}

	const onSelectionChange = (values: ICostsBuildingAccessResponse[]) =>
		onSelectionChangePreventDefaultsRemoval(
			values,
			selectedValues,
			buildingAccessResponse,
			setSelectedValues,
			intl.formatMessage({ id: 'BY_DEFAULT.DELETE.WARNING' })
		)

	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
	/// //////////////////////////////////// EQUIPMENT AND CONSTRUCTION PAGE BODY ////////////////////////////////////////
	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	return (
		<UfinetBox>
			<HardFilterWrapper
				ref={upperFilterRef}
				actionButtonRef={searchButtonRef}
				search={fetchAndStore}
				networkAreaType={AreaType.ACCESS_AREA}
				actionButtons={getActionButtons()}
			/>
			<UfinetSectionBox>
				<Table
					selectedValues={selectedValues}
					onSelectionChange={permissions.canDelete ? onSelectionChange : undefined}
					onRowEditComplete={permissions.canUpdate ? onRowEditComplete : undefined}
					cols={cols}
					content={sortData(buildingAccessResponse)}
					sortField={sortField}
					sortOrder={sortOrder}
					headerButtons={getHeaderButtons()}
					onFilterClear={() => {
						clientNameFilter.reset()
						areaNameFilter.reset()
						updatedByFilter.reset()
					}}
					emptyMessage={loadingRecords ? intl.formatMessage({ id: 'LOADING_DOTS' }) : undefined}
					lazySettings={{
						...pagingData,
						loading: loadingRecords,
						onPage: handlePage,
						onSort: handleSort,
						onFilter: handleFilter,
					}}
				/>
			</UfinetSectionBox>
			{/* Modals */}
			<UfinetModal
				size="xl"
				show={modal}
				handleClose={hideModal}
				title={intl.formatMessage({ id: 'MODAL.UPLOAD_EXCEL.TITLE' })}
			>
				<UploadExcelModalBody
					xlsEmptyDownloadCallback={_buildingAccessCostsService.downloadEmptyListXLS}
					xlsUploadCallback={_buildingAccessCostsService.uploadXLS}
					onXlsUploaded={fetchAndStore}
					hideModal={hideModal}
				/>
			</UfinetModal>
			<UfinetModal
				size="lg"
				show={newModal}
				handleClose={hideNewModal}
				title={intl.formatMessage({ id: 'MODAL.NEW.REGISTER.TITLE' })}
			>
				<NewBuildingAccessModalBody findCosts={fetchAndStore} hideModal={hideNewModal} />
			</UfinetModal>
		</UfinetBox>
	)
}
export { BuildingAccessPage }
